import { useState, useEffect } from 'react';
import { ProjectData, NotificationRecipient } from '../types';
import { useNotificationContext } from '../contexts/NotificationContext';
import { NotificationManager } from './NotificationManager';
import { X } from 'lucide-react';

interface NotificationPreferencesModalProps {
  project: ProjectData;
  onUpdate: (formData: Partial<ProjectData['formData']>) => Promise<void>;
  isOpen: boolean;
  onClose: () => void;
}

export function NotificationPreferencesModal({ project, onUpdate, isOpen, onClose }: NotificationPreferencesModalProps) {
  const { showSuccess, showError } = useNotificationContext();
  const [isUpdating, setIsUpdating] = useState(false);
  const [notifications, setNotifications] = useState<NotificationRecipient[]>(project?.notifications || []);
  
  // onUpdate is kept for backward compatibility but not used anymore
  void onUpdate;

  // Update local state when project changes
  useEffect(() => {
    if (project?.notifications) {
      setNotifications(project.notifications);
    }
  }, [project]);

  const handleNotificationsChange = (updatedNotifications: NotificationRecipient[]) => {
    setNotifications(updatedNotifications);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsUpdating(true);
    try {
      // Call updateProject endpoint to save notifications
      const response = await fetch(`https://us-central1-ima-onboard.cloudfunctions.net/updateProject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jiraKey: project.jiraKey,
          updates: {
            notifications
          }
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update notifications');
      }
      
      showSuccess('Preferences Updated', 'Your notification preferences have been saved successfully');
      onClose(); // Close modal after successful update
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      showError('Update Failed', 'Failed to update notification preferences. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">📬 Notification Preferences</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-sm text-gray-600 mb-4">
              Manage who receives updates about your project. You can add multiple email addresses or phone numbers for your team members.
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 text-xs text-blue-800">
              <p>
                💡 <strong>Tip:</strong> Add notifications for your team members, project managers, or anyone who needs to stay informed about project progress. Each notification can be individually enabled or disabled.
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <NotificationManager
                notifications={notifications}
                onChange={handleNotificationsChange}
              />

              {/* Information Box */}
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-xs text-gray-600">
                <p>
                  <strong>Note:</strong> These notification preferences are optional. You can always change them later or contact us directly if you have any questions about your project status.
                </p>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isUpdating}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdating ? 'Saving...' : 'Save Notification Preferences'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
